import { type ChartDataset } from "chart.js";
import { isNil } from "lodash";

export const UPPER_MODIFIER = 1.04;
export const LOWER_MODIFIER = 0.96;

export const checkIfClose = (
  val1: number | undefined,
  val2: number | undefined,
) => {
  if (!val1 || !val2) {
    return false;
  }

  return Math.abs(val1 / val2) / val2 < 0.04;
};

const calculatePoint = (
  value: number | undefined,
  minMax: { min: number; max: number },
) => {
  if (value === undefined) {
    return NaN;
  }

  // If value is 0, set it to 10% of the max value for it to show up
  if (value === 0) {
    value = minMax.max * 0.1;
  }

  return 100 - ((value - minMax.min) / (minMax.max - minMax.min)) * 100;
};

type Point = {
  left: number | undefined;
  center: number | undefined;
  right: number | undefined;
};

const buildPointAttribute = (
  points: Point[],
  point: Point,
  minMax: { min: number; max: number },
) => {
  const leftPoint = calculatePoint(point.left, minMax);
  const centerPoint = calculatePoint(point.center, minMax);
  const rightPoint = calculatePoint(point.right, minMax);
  const attribute = `
    ${Number.isFinite(leftPoint) ? `0,${leftPoint}` : ""}
    ${Number.isFinite(centerPoint) ? `50,${centerPoint}` : ""}
    ${Number.isFinite(rightPoint) ? `100,${rightPoint}` : ""}
  `;

  // This complicated logic is for the shaded chart area
  if (points.length > 1) {
    const startPoint = calculatePoint(points[0].left, minMax);
    const midPoint = calculatePoint(points[0].center, minMax);
    const endPoint = calculatePoint(points[0].right, minMax);
    const startPointAttribute = Number.isNaN(leftPoint)
      ? `50,${midPoint}`
      : `0,${startPoint}`;
    const endPointAttribute = Number.isNaN(rightPoint)
      ? `50,${midPoint}`
      : `100,${endPoint}`;
    return `
      ${startPointAttribute}
      ${attribute}
      ${endPointAttribute}
    `;
  }

  return attribute;
};

export const getMinMaxOfDatasets = (
  datasets: ChartDataset<any>[],
  dataIndex: number,
) => {
  const extractValues = (index: number) =>
    datasets
      .map((dataset) => dataset.data[index]?.y)
      .filter((value) => value !== undefined);

  const leftValues = extractValues(dataIndex - 1);
  const values = extractValues(dataIndex);
  const rightValues = extractValues(dataIndex + 1);

  const allValues = [...leftValues, ...values, ...rightValues];

  const min = Math.min(...allValues) * 0.75;
  const max = Math.max(...allValues) * 1.25;

  return { min, max };
};

const baseStyles = {
  fill: "transparent",
  "stroke-width": "2",
};

export const createPolylineEl = (
  dataIndex: number,
  minMax: { min: number; max: number },
  datasets: ChartDataset<any, any>[],
  modifier: number,
  style: {
    [key: string]: string;
  },
) => {
  if (datasets.filter(Boolean).length === 0) {
    return "";
  }
  style = { ...baseStyles, ...style };

  const points: any[] = [];
  if (datasets.length === 1) {
    if (isNil(datasets[0].data[dataIndex - 1]?.y)) {
      return "";
    }
    points.push({
      left: datasets[0].data[dataIndex - 1]?.y * modifier,
      center: datasets[0].data[dataIndex]?.y * modifier,
      right: datasets[0].data[dataIndex + 1]?.y * modifier,
    });
  } else if (datasets.length === 2) {
    datasets.forEach((dataset) => {
      if (isNil(dataset.data[dataIndex - 1]?.y)) {
        return;
      }
      if (dataset.fill === "+1") {
        points.push({
          left: dataset.data[dataIndex - 1]?.y * LOWER_MODIFIER,
          center: dataset.data[dataIndex]?.y * LOWER_MODIFIER,
          right: dataset.data[dataIndex + 1]?.y * LOWER_MODIFIER,
        });
      } else {
        points.push({
          left: dataset.data[dataIndex - 1]?.y * UPPER_MODIFIER,
          center: dataset.data[dataIndex]?.y * UPPER_MODIFIER,
          right: dataset.data[dataIndex + 1]?.y * UPPER_MODIFIER,
        });
      }
    });
  }

  const pointsAttribute = points
    .map((point) => buildPointAttribute(points, point, minMax))
    .join(" ");

  return `
    <polyline
      points="${pointsAttribute}"
      style="
        ${Object.entries(style)
          .map(([key, value]) => `${key}:${value};`)
          .join(" ")}"
    />
  `;
};
